import { Link, NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { SiteLayout } from '../../components/SiteLayout.tsx';

/**
 * Admin i samme ramme som resten av nettsiden (SiteLayout): topplinje og bunnlinje,
 * overskrift med navn, klubb og «Logg ut», og faner som på Min side.
 */
export function AdminLayout() {
  const loc = useLocation();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });

  if (me.isLoading) {
    return (
      <SiteLayout>
        <div className="max-w-4xl mx-auto px-4 py-6 text-slate-400">Laster…</div>
      </SiteLayout>
    );
  }
  if (me.isError) return <Navigate to="/admin/login" replace />;

  const role = me.data?.role ?? 'admin';
  const isRegistrar = role === 'registrar';

  // Registrar har ikke tilgang til admin-sider — sender tilbake til min-side.
  if (isRegistrar) {
    const path = loc.pathname;
    const allowed =
      path === '/admin' ||
      path.startsWith('/admin/min-side') ||
      path === '/admin/login';
    if (!allowed) {
      return <Navigate to="/admin/min-side" replace />;
    }
    if (path === '/admin') {
      return <Navigate to="/admin/min-side" replace />;
    }
  }

  // Admin åpnes via kontoen — «Logg ut» avslutter begge øktene (ellers slipper
  // kontoen rett inn i admin igjen).
  async function logout() {
    await api.logout().catch(() => undefined);
    await api.kontoLogout().catch(() => undefined);
    window.location.href = '/';
  }

  // Som fanene på Min side; lista kan rulles sidelengs på mobil.
  const tab = ({ isActive }: { isActive: boolean }) =>
    `shrink-0 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 ${
      isActive ? 'text-sky-300 border-sky-400' : 'text-slate-400 border-transparent hover:text-slate-200'
    }`;

  const klubb = isRegistrar ? 'registrar' : me.data?.foreningName ?? 'Alle klubber';

  return (
    <SiteLayout>
      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h1 className="text-xl font-bold">
              <Link to={isRegistrar ? '/admin/min-side' : '/admin'} className="hover:text-sky-300">
                {isRegistrar ? 'Min side' : 'Admin'}
              </Link>
            </h1>
            <p className="text-sm text-slate-400 truncate">
              {me.data?.fullName ?? me.data?.username}
              <span className="text-slate-500"> • {klubb}</span>
            </p>
          </div>
          <button
            onClick={logout}
            className="px-3 py-1.5 text-sm bg-slate-800 rounded-lg hover:bg-slate-700 shrink-0"
          >
            Logg ut
          </button>
        </div>
        <div className="border-b border-slate-800 mb-4">
          <nav className="flex overflow-x-auto -mb-px [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {isRegistrar ? (
              <>
                <NavLink to="/admin/min-side" end className={tab}>
                  Forside
                </NavLink>
                <NavLink to="/admin/min-side/baater" className={tab}>
                  Mine båter
                </NavLink>
                <NavLink to="/admin/min-side/regattaer" className={tab}>
                  Regattaer
                </NavLink>
              </>
            ) : (
              <>
                <NavLink to="/admin/races" className={tab}>
                  Regattaer
                </NavLink>
                <NavLink to="/admin/boats" className={tab}>
                  Båter
                </NavLink>
                <NavLink to="/admin/trackers" className={tab}>
                  Trackere
                </NavLink>
                <NavLink to="/admin/classes" className={tab}>
                  Klasser
                </NavLink>
                <NavLink to="/admin/merker" className={tab}>
                  Merker
                </NavLink>
                <NavLink to="/admin/baner" className={tab}>
                  Baner
                </NavLink>
                <NavLink to="/admin/admins" className={tab}>
                  Interne brukere
                </NavLink>
                <NavLink to="/admin/kontoer" className={tab}>
                  Seilere
                </NavLink>
                <NavLink to="/admin/foreninger" className={tab}>
                  Foreninger
                </NavLink>
              </>
            )}
          </nav>
        </div>
        <Outlet />
      </main>
    </SiteLayout>
  );
}
